import { CSSObject } from '@emotion/react';
import {
    ColorOption,
    HoneycombTheme,
    ResponsiveStyleValue,
    createResponsiveStyles,
    mergeResponsiveStyleValues,
    useHoneycombTheme,
} from '../../theming';
import { DividerOrientation, DividerVariant } from './Divider.types';

function getResponsiveStyles(theme: HoneycombTheme, color: ColorOption, variant: DividerVariant) {
    return (value: { orientation: DividerOrientation; thickness: number }): CSSObject => {
        const { orientation = 'horizontal', thickness: thicknessProp } = value;

        const colorValue = theme.fn.getColor(color);
        const thickness = `${thicknessProp}px`;

        const vertical = orientation === 'vertical';
        const horizontal = orientation === 'horizontal';

        return {
            ...(horizontal
                ? {
                      border: 0,
                      borderTopWidth: thickness,
                      borderTopColor: colorValue,
                      borderTopStyle: variant,
                  }
                : null),
            ...(vertical
                ? {
                      border: 0,
                      alignSelf: 'stretch',
                      height: 'auto',
                      borderLeftWidth: thickness,
                      borderLeftColor: colorValue,
                      borderLeftStyle: variant,
                  }
                : null),
            '> [data-label]': {
                display: 'flex',
                alignItems: 'center',
                '&::before': {
                    content: '""',
                    flex: 1,
                    marginRight: '16px',
                    borderTop: `${thickness} ${variant} ${colorValue}`,
                },
                '&::after': {
                    content: '""',
                    flex: 1,
                    marginLeft: '16px',
                    borderTop: `${thickness} ${variant} ${colorValue}`,
                },
            },
        };
    };
}

export interface DividerStylesParams {
    color: ColorOption;
    orientation: ResponsiveStyleValue<DividerOrientation>;
    thickness: ResponsiveStyleValue<number>;
    variant: DividerVariant;
    withLabel: boolean;
}

type StyleClasses = 'root';

export function useStyles({ color, orientation, thickness, variant, withLabel }: DividerStylesParams): {
    styles: { [key in StyleClasses]: CSSObject };
} {
    const { theme } = useHoneycombTheme();

    const root: CSSObject = {
        ...createResponsiveStyles(
            [
                {
                    value: mergeResponsiveStyleValues({ orientation, thickness }, theme),
                    getStyles: getResponsiveStyles(theme, color, variant),
                },
            ],
            theme
        ),
        ...(withLabel && {
            borderTop: 0,
        }),
    };

    return {
        styles: {
            root,
        },
    };
}
